import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer das **Upload-Panel der Personalakte**
 * (`employee-file-add-panel`). Gestartet ueber den `<app-dialog-help-button>` im
 * Panel-Kopf — bewusst ein EIGENER Fragezeichen-Knopf neben dem der Akte
 * (`EMPLOYEE_FILE_HELP_TOUR`).
 *
 * Warum getrennt und nicht als Kapitel der Akte-Tour: das Panel ERSETZT den
 * Blaetter-Bereich. Seine Felder existieren erst, wenn es offen ist, und sie
 * haengen zusaetzlich am Aktenteil, den die Rail vorher gewaehlt hat. Eine Tour,
 * die von der Akte aus das Panel synthetisch aufklappt (Muster
 * `CHANGE_REQUESTS_HELP_TOUR`), wuerde hier ein halb ausgefuelltes Formular
 * hinterlassen, sobald der Nutzer abbricht — bei einem Schreibvorgang mit
 * Dateiauswahl ist das der falsche Handel.
 *
 * Kein `launchContext`: das Panel oeffnet aus „Dokument hinzufuegen" (Akte) bzw.
 * „Abrechnung hinzufuegen" (Lohn) oder aus dem Zeilenmenue („Umbenennen &
 * Gueltigkeit"). Die Tour setzt es also als offen voraus.
 *
 * **Vier Betriebsarten, deshalb traegt fast jeder Schritt ein `skipIf`:**
 *
 * | Betriebsart | sichtbare Schritte |
 * |---|---|
 * | `create` in der Akte, Aktenteil OHNE Gueltigkeitspflicht | welcome, target, file, note, visibility, submit |
 * | `create` in der Akte, Aktenteil MIT Gueltigkeitspflicht (Qualifikation, Unterweisung, Gesundheit) | zusaetzlich proof |
 * | `create` Lohnunterlage (Klasse 3) | welcome, target, period, file, note, visibility, submit |
 * | `edit` („Umbenennen & Gueltigkeit") | welcome, target, section, filename, proof, note, visibility, submit |
 * | Selbstbedienung (`selfService`) | ohne visibility |
 *
 * Die Pruefung laeuft ueber DOM-Existenz des Ankers, nicht ueber geratene
 * Zustaende — dieselbe Regel wie in der Akte-Tour („nicht sichtbar heisst nicht
 * vorhanden"). `welcome` und `submit` sind die einzigen bedingungslosen Schritte.
 *
 * **Zwei Schritte haben eine Textvariante fuer den Bearbeiten-Modus.** „Dokument
 * hochladen" und „Hochladen" sind dort falsch: das Panel heisst „Umbenennen &
 * Gueltigkeit", und der Knopf sagt „Speichern". Eine Tour, die ein Label zitiert,
 * das nicht auf dem Schirm steht, kostet Vertrauen. Statt zwei zusaetzlicher
 * Schritte mit je einem `skipIf` traegt derselbe Schritt zwei Textvarianten —
 * Muster `TASK_DETAIL_HELP_TOUR` mit `audioStepId`.
 *
 * Voice-Over: `scripts/voiceover-manifest.json` → `employee-file-add-help`.
 * Cue-Daten landen in `src/assets/onboarding/audio/de/employee-file-add-help/`;
 * `hasCues` setzt die Laufzeit aus dem Audio-Manifest, nicht diese Datei.
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

/** Ist der Anker gerade im DOM? Grundlage aller `skipIf` hier. */
const missing = (anchor: string): boolean =>
  !document.querySelector(`[onboardingAnchor="${anchor}"]`);

/**
 * Laeuft das Panel im Bearbeiten-Modus?
 *
 * Erkannt am Dateinamen-Feld — es existiert **nur** bei `isEdit()`. Bewusst
 * NICHT am Knopftext oder an der Ueberschrift: die sind uebersetzt und aendern
 * sich mit der Sprache (dieselbe Ueberlegung wie `data-primary-kind` im
 * Aufgaben-Detail-Dialog).
 */
const isEditMode = (): boolean =>
  !missing(AnchorId.EmployeeFilePanelFilename);

/** `''` im Anlege-Modus, `_EDIT` beim Bearbeiten. */
const modeSuffix = (): string => (isEditMode() ? '_EDIT' : '');

export const EMPLOYEE_FILE_ADD_HELP_TOUR: TourConfig = {
  id: TourFeature.EmployeeFileAddHelp,
  version: 1,
  title: 'EMPLOYEE_FILE_ADD_HELP_TOUR_TITLE',
  description: 'EMPLOYEE_FILE_ADD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 120,
  category: 'feature-discovery',
  // Pflicht hier: von zehn Schritten laufen je Betriebsart nur sechs bis acht.
  // Ohne das zaehlte das Popover „2 / 10 → 6 / 10 → 8 / 10" und der Nutzer
  // haelt uebersprungene Schritte fuer verlorene (gemessen 2026-08-14).
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'EMPLOYEE_FILE_ADD_HELP_TOUR_TITLE',
      steps: [
        {
          // Ueberblick. Popover NEBEN dem Panel (`popoverOutsideDialog`), sonst
          // verdeckt es genau das Formular, von dem es spricht.
          id: 'welcome',
          anchor: AnchorId.EmployeeFilePanel,
          spotlightAnchor: AnchorId.EmployeeFilePanel,
          // Variante, weil im Bearbeiten-Modus nichts hochgeladen wird.
          title: () => `TUTORIAL.EMPLOYEE_FILE_ADD.WELCOME.TITLE${modeSuffix()}`,
          content: () => `TUTORIAL.EMPLOYEE_FILE_ADD.WELCOME.CONTENT${modeSuffix()}`,
          audioStepId: () => (isEditMode() ? 'welcome-edit' : 'welcome'),
          placement: 'right',
          dialogContext: { ...DIALOG_CTX, popoverOutsideDialog: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Der Aktenteil ist beim Hinzufuegen schon entschieden — die Kopfzeile
          // ist die einzige Stelle, an der das noch steht.
          id: 'target',
          anchor: AnchorId.EmployeeFilePanelTarget,
          spotlightAnchor: AnchorId.EmployeeFilePanelTarget,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.TARGET.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.TARGET.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Nur `edit`: Verschieben verlangt Schreibrecht im Ziel, deshalb
          // stehen in der Auswahl nur Aktenteile mit `CanUpload`.
          id: 'section',
          anchor: AnchorId.EmployeeFilePanelSection,
          spotlightAnchor: AnchorId.EmployeeFilePanelSection,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.SECTION.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.SECTION.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelSection),
        },
        {
          // Nur `edit`: der Anzeigename, nicht die Datei selbst — die bleibt
          // unveraenderlich, weil an ihr die Pruefsumme des Protokolls haengt.
          id: 'filename',
          anchor: AnchorId.EmployeeFilePanelFilename,
          spotlightAnchor: AnchorId.EmployeeFilePanelFilename,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.FILENAME.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.FILENAME.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelFilename),
        },
        {
          // Nur Lohnunterlage: Periode statt Gueltigkeit. Ein Jahresdokument
          // laesst den Monat leer — das ist eine gueltige Angabe, kein Versaeumnis.
          id: 'period',
          anchor: AnchorId.EmployeeFilePanelPeriod,
          spotlightAnchor: AnchorId.EmployeeFilePanelPeriod,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.PERIOD.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.PERIOD.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelPeriod),
        },
        {
          // Nur `create`: Kern des Vorgangs. Formate und Groesse pruefen lokal,
          // die Ablehnung erscheint deshalb am Feld und nicht als Toast.
          id: 'file',
          anchor: AnchorId.EmployeeFilePanelFile,
          spotlightAnchor: AnchorId.EmployeeFilePanelFile,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.FILE.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.FILE.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelFile),
        },
        {
          // Nachweis-Trio. Der Dauerrahmen sitzt auf „Gueltig bis" — dem
          // Pflichtfeld, aus dem die Ablauf-Ueberwachung entsteht und um das
          // der Schritt fachlich geht. Die beiden Nebenfelder sind bewusst KEIN
          // Zusatz-Spotlight, sondern Cues: sie werden gepulst, wenn die
          // Vertonung sie nennt, und geben den Rahmen danach wieder frei.
          //
          // Vorher trugen alle drei sowohl Spotlight als auch Cue — das ergab
          // einen zweiten, inneren Ring auf demselben Element („zwei
          // Highlights", CONTEXTUAL_HELP_PATTERN.md). Der Spec-Fall
          // „legt keinen Cue auf denselben Bereich wie den Schritt-Spotlight"
          // haelt das fest.
          id: 'proof',
          anchor: AnchorId.EmployeeFilePanelValidUntil,
          spotlightAnchor: AnchorId.EmployeeFilePanelValidUntil,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.PROOF.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.PROOF.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelProofKind),
        },
        {
          id: 'note',
          anchor: AnchorId.EmployeeFilePanelNote,
          spotlightAnchor: AnchorId.EmployeeFilePanelNote,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.NOTE.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.NOTE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelNote),
        },
        {
          // Entfaellt in der Selbstbedienung: dort IST der Hochladende der
          // Betroffene, „Fuer … sichtbar" waere eine sinnlose Frage.
          id: 'visibility',
          anchor: AnchorId.EmployeeFilePanelVisibility,
          spotlightAnchor: AnchorId.EmployeeFilePanelVisibility,
          title: 'TUTORIAL.EMPLOYEE_FILE_ADD.VISIBILITY.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE_ADD.VISIBILITY.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => missing(AnchorId.EmployeeFilePanelVisibility),
        },
        {
          // Abschluss: was NACH dem Klick passiert (Virenpruefung, „Wird
          // geprueft", Protokoll). Der Fusszeilen-Hinweis ist bewusst KEIN
          // Zusatz-Spotlight, sondern ein Cue — er wird erst gepulst, wenn die
          // Vertonung von der Pruefung spricht. Als Dauer-Rahmen konkurrierte
          // er die ganze Zeit mit dem Knopf.
          id: 'submit',
          anchor: AnchorId.EmployeeFilePanelSubmit,
          spotlightAnchor: AnchorId.EmployeeFilePanelSubmit,
          // Variante, weil der Knopf beim Bearbeiten „Speichern" heisst und es
          // dort keine Virenpruefung gibt — nur die Metadaten aendern sich.
          title: () => `TUTORIAL.EMPLOYEE_FILE_ADD.SUBMIT.TITLE${modeSuffix()}`,
          content: () => `TUTORIAL.EMPLOYEE_FILE_ADD.SUBMIT.CONTENT${modeSuffix()}`,
          audioStepId: () => (isEditMode() ? 'submit-edit' : 'submit'),
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
